import { dayjsUTC } from "../../utils/dates"
import type { DatasetEntry } from "./timeSeriesConstants"

/**
 * Fills weekly gaps in time series data
 * @param sortedDates - Array of date strings sorted chronologically
 * @param dataByDate - Map of date string to data entries
 * @param buildEntry - Function to build a dataset entry for a given date
 * @param buildNullEntry - Function to build a null/gap entry
 * @returns Object containing filled dataset and dense dates array
 */
export function fillWeeklyGaps<T>(
  sortedDates: string[],
  dataByDate: Map<string, T>,
  buildEntry: (dateObj: Date, data: T | undefined) => DatasetEntry,
  buildNullEntry: (dateObj: Date) => DatasetEntry
): { dataset: DatasetEntry[]; dates: Date[] } {
  const denseDates: Date[] = []
  const filledDataset: DatasetEntry[] = []
  let lastEndDate: ReturnType<typeof dayjsUTC> | null = null

  sortedDates.forEach(endDateStr => {
    const endDate = dayjsUTC(endDateStr)

    // Check for gaps > 10 days (implying missed weekly samples)
    if (lastEndDate) {
      const diffDays = endDate.diff(lastEndDate, "day")
      if (diffDays > 10) {
        // Calculate how many weekly filler dates we need
        const gapWeeks = Math.floor((diffDays - 3) / 7) - 1

        for (let i = 1; i <= gapWeeks; i++) {
          const fillerDate = lastEndDate.add(i * 7, "day")
          const fillerDateObj = fillerDate.toDate()
          denseDates.push(fillerDateObj)
          filledDataset.push(buildNullEntry(fillerDateObj))
        }
      }
    }

    const dateObj = endDate.toDate()
    denseDates.push(dateObj)
    lastEndDate = endDate

    const data = dataByDate.get(endDateStr)
    filledDataset.push(buildEntry(dateObj, data))
  })

  return { dataset: filledDataset, dates: denseDates }
}
